import { useCallback, useEffect, useRef, useState } from 'react';
import { useSignalR } from '../contexts/SignalRContext';

// Signals invalidate API data; received messages never replace rows or dispatch Redux actions.
export default function useLiveUpdates({ enabled = true, type, scope, blocked = false, refresh, validate = () => true, matches }) {
  const { status, clientId, subscribe, publish: send } = useSignalR();
  const [queued, setQueued] = useState(false);
  const [warning, setWarning] = useState('');
  const options = useRef({ blocked, refresh, validate, matches });
  options.current = { blocked, refresh, validate, matches };
  const session = useRef(null);

  useEffect(() => {
    setQueued(false);
    setWarning('');
    if (!enabled || !type || !scope) return undefined;
    let disposed = false;
    let refreshTimer;
    let refreshing = false;
    let dirty = false;
    const seen = new Set();
    const flush = async () => {
      clearTimeout(refreshTimer);
      refreshTimer = undefined;
      if (disposed || !dirty || refreshing || options.current.blocked) return;
      dirty = false;
      refreshing = true;
      setQueued(false);
      let success = false;
      try { success = await options.current.refresh(); } catch { /* Retry without breaking edits. */ }
      refreshing = false;
      if (disposed) return;
      if (!success) {
        dirty = true;
        setQueued(true);
        setWarning('Live refresh failed. Retrying shortly; manual Refresh is also available.');
      } else {
        setWarning((current) => current.startsWith('Live refresh failed.') ? '' : current);
      }
      if (dirty) refreshTimer = setTimeout(flush, success ? 300 : 5000);
    };
    const requestRefresh = () => {
      dirty = true;
      setQueued(true);
      if (!refreshing && refreshTimer === undefined) {
        refreshTimer = setTimeout(flush, 300);
      }
    };
    const publish = async (payload) => {
      const result = await send({ type, payload: { ...payload, version: 1, scope } });
      return result;
    };
    session.current = { publish, requestRefresh, resume: () => { if (!refreshTimer) void flush(); } };
    const unsubscribe = subscribe((action) => {
      const matchesTopic = options.current.matches
        ? options.current.matches(action) : action?.type === type && action.payload?.scope === scope;
      if (disposed || !matchesTopic || action?.payload?.version !== 1 || !options.current.validate(action)) return;
      const { id, clientId: sender } = action.payload;
      if (typeof id !== 'string' || !id || id.length > 128 || typeof sender !== 'string' || !sender || sender.length > 128 || sender === clientId) return;
      const eventKey = JSON.stringify([sender, id]);
      if (seen.has(eventKey)) return;
      seen.add(eventKey);
      if (seen.size > 200) seen.delete(seen.values().next().value);
      requestRefresh();
    });
    return () => {
      disposed = true;
      session.current = null;
      clearTimeout(refreshTimer);
      unsubscribe();
    };
  }, [enabled, type, scope, clientId, subscribe, send]);

  useEffect(() => { if (status === 'Connected') session.current?.requestRefresh(); }, [status, enabled, type, scope]);

  useEffect(() => { if (!blocked) session.current?.resume(); }, [blocked]);
  const publish = useCallback((payload) => session.current?.publish(payload), []);
  return { status, queued, warning, publish, dismissWarning: () => setWarning('') };
}
